import { useEffect, useState, useCallback } from 'react'
import { adminApi } from '../../api/adminApi'
import { Spinner } from '../ui/Spinner'
import type { PostCountryViewStat, CountryPostStat, CountryViewStat } from '../../types'

interface Props {
  countryPostCounts: CountryPostStat[]
  topCountries: CountryViewStat[]
}

function flagEmoji(code: string) {
  return code
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join('')
}

function ViewBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="geo-bar-track">
      <div className="geo-bar-fill" style={{ width: `${pct}%` }} />
    </div>
  )
}

function AccordionRow({ post, maxViews }: { post: PostCountryViewStat; maxViews: number }) {
  const [open, setOpen] = useState(false)
  const maxCountry = post.countryBreakdown[0]?.viewCount ?? 1

  return (
    <div className={`acc-row${open ? ' acc-row--open' : ''}`}>
      <button
        className="acc-header"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <div className="acc-header-left">
          <span className="acc-chevron">{open ? '▾' : '▸'}</span>
          <span className="acc-post-title">{post.postTitle}</span>
        </div>
        <div className="acc-header-right">
          <span className="acc-views-badge">{post.totalViews.toLocaleString()} views</span>
          <ViewBar value={post.totalViews} max={maxViews} />
        </div>
      </button>

      {open && (
        <div className="acc-body">
          {post.countryBreakdown.length === 0 ? (
            <p className="geo-empty-row">No country data recorded for this post.</p>
          ) : (
            <div className="geo-table-wrap">
            <table className="geo-table">
              <thead>
                <tr>
                  <th className="geo-th">Country</th>
                  <th className="geo-th geo-th--code">Code</th>
                  <th className="geo-th geo-th--bar">Distribution</th>
                  <th className="geo-th geo-th--num">Views</th>
                </tr>
              </thead>
              <tbody>
                {post.countryBreakdown.map((row, i) => (
                  <tr key={row.countryCode} className={i % 2 === 0 ? 'geo-tr-even' : ''}>
                    <td className="geo-td">
                      <span className="geo-flag">{flagEmoji(row.countryCode)}</span>
                      {row.country}
                    </td>
                    <td className="geo-td geo-td--code">
                      <span className="geo-code-pill">{row.countryCode}</span>
                    </td>
                    <td className="geo-td geo-td--bar">
                      <ViewBar value={row.viewCount} max={maxCountry} />
                    </td>
                    <td className="geo-td geo-td--num">{row.viewCount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function PostCountryViewsSection({ countryPostCounts, topCountries }: Props) {
  const [items, setItems] = useState<PostCountryViewStat[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async (p: number) => {
    setLoading(true)
    try {
      const res = await adminApi.getPostCountryViews(p, 5)
      setItems(res.data)
      setTotalPages(res.meta.totalPages)
      setTotalCount(res.meta.totalCount)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(page) }, [load, page])

  const maxViews = items[0]?.totalViews ?? 1
  const maxTopCountry = topCountries[0]?.viewCount ?? 1
  const maxPostCount = countryPostCounts[0]?.postCount ?? 1

  return (
    <div className="geo-sections">

      {/* ── Post View Count by Country ─────────────────────────── */}
      <div className="geo-card">
        <div className="geo-card-head geo-card-head--blue">
          <div className="geo-card-head-left">
            <span className="geo-card-icon">🌍</span>
            <div>
              <h2 className="geo-card-title">Post View Count by Country</h2>
              <p className="geo-card-sub">Per-post geographic breakdown — click a post to expand</p>
            </div>
          </div>
          {totalCount > 0 && (
            <span className="geo-total-badge">{totalCount} posts</span>
          )}
        </div>

        <div className="geo-card-body">
          {loading ? (
            <div className="geo-spinner-wrap"><Spinner label="Loading…" /></div>
          ) : items.length === 0 ? (
            <p className="geo-empty">No view data recorded yet.</p>
          ) : (
            <>
              <div className="acc-list">
                {items.map(post => (
                  <AccordionRow key={post.postSlug} post={post} maxViews={maxViews} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="geo-pagination">
                  <button
                    className="geo-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    ← Prev
                  </button>
                  <div className="geo-page-dots">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        className={`geo-dot${page === i + 1 ? ' geo-dot--active' : ''}`}
                        onClick={() => setPage(i + 1)}
                        aria-label={`Page ${i + 1}`}
                      />
                    ))}
                  </div>
                  <button
                    className="geo-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Posts Viewed per Country + Top Countries side-by-side ─ */}
      <div className="geo-two-col">

        {/* Posts per country */}
        {countryPostCounts.length > 0 && (
          <div className="geo-card">
            <div className="geo-card-head geo-card-head--teal">
              <div className="geo-card-head-left">
                <span className="geo-card-icon">📌</span>
                <div>
                  <h2 className="geo-card-title">Posts Viewed per Country</h2>
                  <p className="geo-card-sub">Distinct posts read by each country</p>
                </div>
              </div>
            </div>
            <div className="geo-card-body">
              <div className="geo-table-wrap">
              <table className="geo-table">
                <thead>
                  <tr>
                    <th className="geo-th">Country</th>
                    <th className="geo-th geo-th--code">Code</th>
                    <th className="geo-th geo-th--bar">Distribution</th>
                    <th className="geo-th geo-th--num">Posts</th>
                  </tr>
                </thead>
                <tbody>
                  {countryPostCounts.map((row, i) => (
                    <tr key={row.countryCode} className={i % 2 === 0 ? 'geo-tr-even' : ''}>
                      <td className="geo-td">
                        <span className="geo-flag">{flagEmoji(row.countryCode)}</span>
                        {row.country}
                      </td>
                      <td className="geo-td geo-td--code">
                        <span className="geo-code-pill">{row.countryCode}</span>
                      </td>
                      <td className="geo-td geo-td--bar">
                        <ViewBar value={row.postCount} max={maxPostCount} />
                      </td>
                      <td className="geo-td geo-td--num">{row.postCount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {/* Top countries by views */}
        {topCountries.length > 0 && (
          <div className="geo-card">
            <div className="geo-card-head geo-card-head--indigo">
              <div className="geo-card-head-left">
                <span className="geo-card-icon">📊</span>
                <div>
                  <h2 className="geo-card-title">Top Countries by Views</h2>
                  <p className="geo-card-sub">Highest total page views by country</p>
                </div>
              </div>
            </div>
            <div className="geo-card-body">
              <div className="geo-table-wrap">
              <table className="geo-table">
                <thead>
                  <tr>
                    <th className="geo-th">#</th>
                    <th className="geo-th">Country</th>
                    <th className="geo-th geo-th--bar">Distribution</th>
                    <th className="geo-th geo-th--num">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {topCountries.map((row, i) => (
                    <tr key={row.countryCode} className={i % 2 === 0 ? 'geo-tr-even' : ''}>
                      <td className="geo-td geo-td--rank">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </td>
                      <td className="geo-td">
                        <span className="geo-flag">{flagEmoji(row.countryCode)}</span>
                        {row.country}
                      </td>
                      <td className="geo-td geo-td--bar">
                        <ViewBar value={row.viewCount} max={maxTopCountry} />
                      </td>
                      <td className="geo-td geo-td--num">{row.viewCount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* ── Layout ─────────────────────────────────────────────── */
        .geo-sections { display: flex; flex-direction: column; gap: 20px; }
        .geo-two-col  { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        @media (max-width: 900px) { .geo-two-col { grid-template-columns: 1fr; } }

        /* ── Card shell ─────────────────────────────────────────── */
        .geo-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }

        /* ── Card header variants ───────────────────────────────── */
        .geo-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          gap: 10px;
          flex-wrap: wrap;
        }
        .geo-card-head-left { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
        .geo-card-head--blue   { background: linear-gradient(135deg, #0369a1 0%, #0284c7 100%); }
        .geo-card-head--teal   { background: linear-gradient(135deg, #0f766e 0%, #0d9488 100%); }
        .geo-card-head--indigo { background: linear-gradient(135deg, #4338ca 0%, #4f46e5 100%); }

        .geo-card-icon   { font-size: 1.3rem; line-height: 1; flex-shrink: 0; }
        .geo-card-title  { font-size: 0.88rem; font-weight: 700; color: #fff; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .geo-card-sub    { font-size: 0.7rem; color: rgba(255,255,255,0.72); margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .geo-total-badge {
          font-size: 0.72rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 20px;
          padding: 3px 10px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* ── Card body ──────────────────────────────────────────── */
        .geo-card-body   { padding: 0; }
        .geo-spinner-wrap {
          padding: 32px;
          display: flex;
          justify-content: center;
        }
        .geo-empty {
          padding: 28px;
          text-align: center;
          color: var(--text-muted);
          font-size: 0.85rem;
          margin: 0;
        }

        /* ── Accordion ──────────────────────────────────────────── */
        .acc-list { display: flex; flex-direction: column; }
        .acc-row  { border-bottom: 1px solid var(--border-color); }
        .acc-row:last-child { border-bottom: none; }
        .acc-row--open .acc-header { background: rgba(2, 132, 199, 0.06); }

        .acc-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 11px 14px;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
          box-sizing: border-box;
        }
        .acc-header:hover { background: rgba(2, 132, 199, 0.05); }

        .acc-header-left {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          flex: 1;
        }
        .acc-chevron {
          font-size: 0.75rem;
          color: #0284c7;
          flex-shrink: 0;
          width: 12px;
        }
        .acc-post-title {
          font-size: 0.83rem;
          font-weight: 600;
          color: var(--text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }

        .acc-header-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .acc-views-badge {
          font-size: 0.73rem;
          font-weight: 600;
          color: #0284c7;
          background: rgba(2, 132, 199, 0.1);
          border: 1px solid rgba(2, 132, 199, 0.2);
          border-radius: 20px;
          padding: 2px 8px;
          white-space: nowrap;
        }

        .acc-body { padding: 0 0 4px; }
        .geo-empty-row {
          padding: 12px 20px;
          margin: 0;
          font-size: 0.8rem;
          color: var(--text-muted);
          font-style: italic;
        }

        /* ── Table ──────────────────────────────────────────────── */
        .geo-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .geo-table {
          width: 100%;
          min-width: 300px;
          border-collapse: collapse;
          font-size: 0.83rem;
        }
        .geo-th {
          padding: 7px 10px;
          text-align: left;
          font-size: 0.68rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #0284c7;
          background: rgba(2, 132, 199, 0.06);
          border-bottom: 1px solid rgba(2, 132, 199, 0.15);
          white-space: normal;
        }
        .geo-th--num  { text-align: right; }
        .geo-th--code { width: 50px; }
        .geo-th--bar  { width: 80px; }

        .geo-td {
          padding: 8px 10px;
          color: var(--text-secondary);
          vertical-align: middle;
        }
        .geo-td--code  { width: 50px; }
        .geo-td--bar   { width: 80px; padding-right: 8px; }
        .geo-td--num   { text-align: right; font-weight: 600; color: var(--text-primary); font-variant-numeric: tabular-nums; white-space: nowrap; }
        .geo-td--rank  { width: 36px; font-size: 1rem; text-align: center; }

        .geo-tr-even { background: rgba(2, 132, 199, 0.025); }
        .geo-table tbody tr:hover { background: rgba(2, 132, 199, 0.06); }

        .geo-flag      { margin-right: 8px; font-size: 1.05rem; }
        .geo-code-pill {
          display: inline-block;
          font-family: 'Courier New', monospace;
          font-size: 0.72rem;
          font-weight: 600;
          color: #0891b2;
          background: rgba(8, 145, 178, 0.1);
          border: 1px solid rgba(8, 145, 178, 0.2);
          border-radius: 4px;
          padding: 1px 6px;
          letter-spacing: 0.05em;
        }

        /* ── Bar ────────────────────────────────────────────────── */
        .geo-bar-track {
          width: 100%;
          height: 6px;
          background: rgba(2, 132, 199, 0.12);
          border-radius: 3px;
          overflow: hidden;
        }
        .geo-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #0284c7, #06b6d4);
          border-radius: 3px;
          transition: width 0.3s ease;
          min-width: 2px;
        }
        /* Bar in accordion header: shrinks but doesn't go below 48px, hidden on very small screens */
        .acc-header-right .geo-bar-track { width: clamp(48px, 15vw, 80px); flex-shrink: 1; }
        @media (max-width: 380px) {
          .acc-header-right .geo-bar-track { display: none; }
        }

        /* ── Pagination ──────────────────────────────────────────── */
        .geo-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 14px 20px;
          border-top: 1px solid var(--border-color);
          background: rgba(2, 132, 199, 0.02);
        }
        .geo-page-btn {
          padding: 5px 14px;
          font-size: 0.78rem;
          font-weight: 500;
          border: 1px solid rgba(2, 132, 199, 0.3);
          border-radius: 6px;
          background: var(--bg-primary);
          color: #0284c7;
          cursor: pointer;
          transition: all 0.15s;
        }
        .geo-page-btn:hover:not(:disabled) {
          background: #0284c7;
          color: #fff;
          border-color: #0284c7;
        }
        .geo-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }

        .geo-page-dots { display: flex; align-items: center; gap: 6px; }
        .geo-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: none;
          background: rgba(2, 132, 199, 0.25);
          cursor: pointer;
          transition: all 0.15s;
          padding: 0;
        }
        .geo-dot--active { background: #0284c7; transform: scale(1.3); }
        .geo-dot:hover:not(.geo-dot--active) { background: rgba(2, 132, 199, 0.5); }

        /* ── Mobile overrides ───────────────────────────────────── */
        @media (max-width: 480px) {
          .geo-card-head   { padding: 12px 12px; }
          .geo-card-sub    { display: none; }
          .acc-header      { padding: 10px 12px; }
          .geo-th, .geo-td { padding: 7px 8px; font-size: 0.78rem; }
          .geo-th--bar, .geo-td--bar { width: 60px; }
          .geo-flag        { font-size: 0.95rem; margin-right: 5px; }
          .geo-pagination  { padding: 10px 12px; gap: 8px; }
          .geo-page-btn    { padding: 4px 10px; font-size: 0.74rem; }
        }
      `}</style>
    </div>
  )
}
